import { CSSResult, html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { i18nContext } from '../../../scripts/context/i18nContext.js';
import { consume } from '@lit/context';
import styles from './remove-article-layer.scss?inline';
import { Messages } from '../../../@types/wishlist.js';
import { productMenuContext } from '../../../scripts/context/menuContext.js';
import { ProductMenuState } from '../../../scripts/context/menu-state.js';
import { sharedCss } from '../../../scripts/style-bundle/shared.js';
import { wishlistService } from '../../../scripts/service/wishlist-service.js';
import { ProductIdentifier } from '../../../scripts/service/product-identifier.js';
import { collectionApi } from '../../../scripts/service/collection-api.js';
import { WishlistAppConfig, wishlistAppConfigContext } from '../../../scripts/context/appConfigContext.js';
import { productsContext } from '../../../scripts/context/wishlistContext.js';
import { ProductsState } from '../../../scripts/context/wishlist-state.js';
import { pushToSnackbar } from '../../../scripts/service/snackbar-service.js';
import { getSinglePictureUrl } from '../../../scripts/service/image-formatter.js';
import { trackRemoveItemFromCollection } from '../../../scripts/service/cube-tracking-layer.js';
import { getBackButton } from '../base-layer/base-layer.js';
import baseLayerStyles from '../base-layer/base-layer.scss?inline';

@customElement('ents-remove-article-layer')
export class RemoveArticleLayer extends LitElement {
  @consume({ context: i18nContext })
  private messages!: Messages;

  @consume({ context: productMenuContext, subscribe: true })
  @property({ attribute: false })
  private productMenuState!: ProductMenuState;

  @consume({ context: productsContext, subscribe: true })
  @property({ attribute: false })
  private productsState!: ProductsState;

  @consume({ context: wishlistAppConfigContext })
  private wishlistAppConfig!: WishlistAppConfig;

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles), unsafeCSS(baseLayerStyles)];
  }

  private backButtonAction() {
    if (this.productMenuState.context.backButtonCallback) {
      this.productMenuState.context.backButtonCallback();
    }
    this.closeLayer();
    this.dispatchEvent(new Event('ents-layer-back', { composed: true, bubbles: true }));
  }

  protected render(): unknown {
    const collectionName = this.productMenuState.context.collection?.name;
    return html`
      <ents-base-layer
        ?showLayer="${this.productMenuState.context.activeMenu === 'remove-article'}"
        @ents-layer-closed="${this.closeLayer}"
      >
        ${this.productMenuState.context.backButtonCallback === undefined
          ? nothing
          : getBackButton(this.messages, 'remove-article-layer', this.backButtonAction.bind(this))}
        <div class="ents-remove-article-layer" data-test-ents-remove-article-layer>
          <h2 class="ents-heading-medium">${this.messages['ents_wishlist_collection_article_remove_title']}</h2>
          <p>${this.messages['ents_wishlist_collection_article_remove_description']?.replace('{}', collectionName!)}</p>
          <button
            class="bdk-button"
            type="button"
            variant="outline"
            size="medium"
            data-test-ents-remove-article-collection-only-button
            @click="${() => this.removeItem(false)}"
          >
            ${this.messages['ents_wishlist_collection_article_remove_listOnly']}
          </button>
          <button
            class="bdk-button"
            type="button"
            variant="outline"
            size="medium"
            data-test-ents-remove-article-collection-and-favorites-button
            @click="${() => this.removeItem(true)}"
          >
            ${this.messages['ents_wishlist_collection_article_remove_listAndFavorites']}
          </button>
        </div>
      </ents-base-layer>
    `;
  }

  private removeItem(removeItemFromFavorites: boolean) {
    const product = this.productMenuState.context.product!;
    const collection = this.productMenuState.context.collection!;
    let articleDeletedText;
    if (removeItemFromFavorites) {
      const data = {
        identifier: ProductIdentifier.createByProduct(product).id,
        syan: product.syan,
        colorId: product.colorId,
      };
      const trackingCallback = () => trackRemoveItemFromCollection(product, collection, 'wishlist_action_menu', true);
      wishlistService.removeFromWishlist(data, trackingCallback).then((result) => {
        if (result) {
          this.productsState.updateAll(
            this.productsState.entities.filter(
              (pr) => !(product.syan === pr.syan && (product.colorId ? product.colorId === pr.colorId : true)),
            ),
          );
        }
      });
      articleDeletedText = this.messages['ents_wishlist_collection_removedArticleFromFavoritesAndCollections'];
    } else {
      this.removeFromCollection();
      articleDeletedText = this.messages['ents_wishlist_collection_removedArticleFromCollection'];
    }
    this.closeLayer();
    pushToSnackbar(
      articleDeletedText!,
      getSinglePictureUrl(this.productMenuState.context.product!.defaultImageUrls[0].url, 'webp'),
    );
  }

  private removeFromCollection() {
    const product = this.productMenuState.context.product!;
    const collection = this.productMenuState.context.collection!;
    collectionApi
      .deleteItem(this.wishlistAppConfig.collectionEndpoint, collection.id, product.syan, product.colorId)
      .then(() => {
        this.productsState.removeFromCollection(product, collection, 'wishlist_action_menu');
      });
  }

  private closeLayer = () => {
    if (this.productMenuState.context.activeMenu === 'remove-article') {
      this.productMenuState.closeMenu();
    }
  };
}
